// Registro de cambios de configuración (autogestión 2026-10-30 §1.2,
// T-tl24) — el contrato de lectura que consume el dashboard y el cuerpo
// que auth-svc le manda a core-api cuando desactiva o reactiva a alguien.
//
// Las filas viven en `audit_events` (core-api) con `event_type`
// `config.<section>.<action>`; acá sólo va la forma del cable. El texto
// «Cambio» (`summary`) se arma en el servidor, en es-CL, a partir del
// tipo de evento y del payload — el cliente nunca interpreta payloads.
import { z } from "zod";
// Secciones del módulo Administración, en el orden del diseño. `activos`
// agrupa tiendas y zonas (el sustantivo lo pone la receta en la UI).
export const configAuditSectionSchema = z.enum([
    "equipo",
    "contexto_ia",
    "activos",
    "checklists",
    "mantenciones",
    "contratistas",
    "enlaces",
    "empresa",
    "plazos",
    "garantias",
]);
// `member`: alguien de la organización, desde el dashboard. `support`:
// el equipo de BitácoraPro (administrador de plataforma, rutas /admin/*,
// scripts). La UI lo muestra como pill «BitácoraPro».
export const configAuditActorKindSchema = z.enum(["member", "support"]);
// `action` es un slug corto (`created`, `member_disabled`…); junto con la
// sección forma el `event_type`. Minúsculas y guión bajo, nada más, para
// que el LIKE por sección del lector no se confunda con un punto extra.
export const configAuditActionSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/);
export const configAuditTargetSchema = z.object({
    // Qué cosa cambió: `asset`, `asset_group`, `user`, `checklist_template`,
    // `maintenance_item`, `provider`, `share_bundle`, `organization`…
    // Texto libre, mismo criterio que `event_type`.
    kind: z.string().min(1).max(64),
    id: z.string().min(1),
    // Cómo lo nombra la persona: código de la tienda, nombre de la zona,
    // email del integrante. Para un enlace compartido nunca el token ni la
    // URL (ver share-bundles.ts).
    label: z.string().max(200).nullable().optional(),
});
export const configAuditActorSchema = z.object({
    userId: z.string().uuid().nullable(),
    email: z.string().nullable(),
    kind: configAuditActorKindSchema,
});
// POST /internal/config-audit (core-api) — lo que auth-svc envía al
// desactivar o reactivar una membresía y al revocar o reenviar una
// invitación. Es un SUBCONJUNTO de lo que recibe el helper
// `writeConfigAuditEvent` de core-api, cerrado a lo que auth-svc emite
// hoy: sección `equipo`, cuatro acciones, un `user` o una `invitation`
// como objetivo y sin antes/después. El helper interno es general; la
// superficie de red no, para que el token interno no sirva para
// fabricar historia en cualquier sección. Un emisor nuevo ensancha este
// contrato con su propia acción, no lo vuelve genérico.
export const internalConfigAuditActionSchema = z.enum([
    "member_disabled",
    "member_enabled",
    "invitation_revoked",
    "invitation_resent",
]);
const internalConfigAuditBaseSchema = z.object({
    organizationId: z.string().uuid(),
    actor: configAuditActorSchema.extend({
        email: z.string().email().max(254).nullable(),
    }),
    section: z.literal("equipo"),
});
// La acción fija la forma del objetivo: una membresía apunta a un `user`
// (su email es lo que la plantilla «Desactivó a …» muestra); una
// invitación apunta a la fila `invitation` con el email invitado como
// etiqueta —null si era una invitación abierta por enlace— para «Revocó
// la invitación a «…»»; con etiqueta null la plantilla de core-api cierra
// la frase por su cuenta («… una invitación por enlace»), porque auth-svc
// no tiene a quién nombrar. Cada rama es `.strict()`: un cuerpo con
// `before`/`after` o cualquier otra clave es un emisor que cambió sin
// ensanchar este contrato (400), no algo que se recorta en silencio.
export const internalConfigAuditRequestSchema = z.discriminatedUnion("action", [
    internalConfigAuditBaseSchema
        .extend({
        action: z.enum(["member_disabled", "member_enabled"]),
        target: configAuditTargetSchema.extend({
            kind: z.literal("user"),
            label: z.string().email().max(254),
        }),
    })
        .strict(),
    internalConfigAuditBaseSchema
        .extend({
        action: z.enum(["invitation_revoked", "invitation_resent"]),
        target: configAuditTargetSchema.extend({
            kind: z.literal("invitation"),
            id: z.string().uuid(),
            label: z.string().email().max(254).nullable(),
        }),
    })
        .strict(),
]);
// GET /organizations/me/config-audit?section=&cursor=&limit=
export const configAuditListQuerySchema = z.object({
    section: configAuditSectionSchema.optional(),
    // Opaco para el cliente: lo devuelve `nextCursor` y se reenvía tal cual.
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const configAuditItemSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    actor: z.object({
        kind: configAuditActorKindSchema,
        // Hoy el email capturado al escribir (audit_events.actor_email); null
        // para eventos de sistema o tokens sin claim de email. El nombre
        // visible lo resuelve el cliente si lo tiene en el equipo.
        name: z.string().nullable(),
    }),
    section: configAuditSectionSchema,
    action: z.string(),
    // Texto «Cambio» en es-CL, listo para mostrar.
    summary: z.string(),
});
export const configAuditListResponseSchema = z.object({
    items: z.array(configAuditItemSchema),
    nextCursor: z.string().nullable(),
});
// GET /organizations/me/config-audit.csv?section=
export const configAuditCsvQuerySchema = configAuditListQuerySchema.pick({ section: true });
//# sourceMappingURL=config-audit.js.map